/**
 * KPI Dashboard Component
 * Displays KPI performance metrics with targets
 * Used in Admin, Employee, and Agent dashboards
 */
import { useState, useEffect } from 'react';
import { 
  TrendingUp, TrendingDown, Target, Users, FileText, 
  GraduationCap, AlertTriangle, CheckCircle, Clock,
  BarChart2, ArrowUpRight, ArrowDownRight, RefreshCw,
  Loader2, Award, Briefcase
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { kpiAPI } from '@/lib/api';

// KPI Targets (should match backend)
const KPI_TARGETS = {
  b2b_app_to_enrollment: 8.0,
  b2c_app_to_enrollment: 12.0,
  b2c_inquiry_to_lead: 25.0,
};

// Status colors
const getStatusColor = (status) => {
  switch(status) {
    case 'on_track': return 'bg-green-100 text-green-600 border-green-500/30';
    case 'below_target': return 'bg-red-100 text-red-600 border-red-500/30';
    case 'needs_attention': return 'bg-teal-100 text-teal-600 border-yellow-500/30';
    default: return 'bg-gray-200 text-gray-500 border-slate-500/30';
  }
};

// KPI Card Component
const KPICard = ({ title, value, target, unit = '%', icon: Icon, status, gap, subtitle }) => {
  const isOnTrack = status === 'on_track';
  const progressValue = Math.min((value / target) * 100, 100);
  
  return (
    <div className={`rounded-xl p-5 border ${isOnTrack ? 'bg-green-500/5 border-green-500/20' : 'bg-red-500/5 border-red-500/20'}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isOnTrack ? 'bg-green-100' : 'bg-red-100'}`}>
            <Icon className={`w-5 h-5 ${isOnTrack ? 'text-green-500' : 'text-red-500'}`} />
          </div>
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-semibold text-gray-800">{value}{unit}</p>
          </div>
        </div>
        <Badge className={getStatusColor(status)}>
          {isOnTrack ? (
            <><CheckCircle className="w-3 h-3 mr-1" /> On Track</>
          ) : (
            <><AlertTriangle className="w-3 h-3 mr-1" /> Below Target</>
          )}
        </Badge>
      </div>
      
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Target: {target}{unit}</span>
          <span className={isOnTrack ? 'text-green-600' : 'text-red-600'}>
            {isOnTrack ? '+' : ''}{-gap}{unit} {isOnTrack ? 'above' : 'below'}
          </span>
        </div>
        <Progress 
          value={progressValue} 
          className={`h-2 ${isOnTrack ? '[&>div]:bg-green-500' : '[&>div]:bg-red-500'}`}
        />
        {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
      </div>
    </div>
  );
};

// Agent KPI Panel (for Agent Dashboard) - Enhanced with Personal Goals & Leaderboard
export const AgentKPIPanel = ({ agentId }) => {
  const [kpiData, setKpiData] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [personalGoal, setPersonalGoal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [newGoal, setNewGoal] = useState(8);
  const [savingGoal, setSavingGoal] = useState(false);
  const [activeTab, setActiveTab] = useState('performance');

  useEffect(() => {
    loadAllData();
  }, [agentId]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [kpiRes, leaderboardRes, goalRes] = await Promise.all([
        kpiAPI.getAgentKPI(agentId),
        kpiAPI.getLeaderboard(),
        kpiAPI.getPersonalGoal()
      ]);
      setKpiData(kpiRes.data);
      setLeaderboard(leaderboardRes.data);
      setPersonalGoal(goalRes.data);
      if (goalRes.data.personal_target) {
        setNewGoal(goalRes.data.personal_target);
      }
    } catch (error) {
      console.error('Failed to load KPI data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGoal = async () => {
    setSavingGoal(true);
    try {
      await kpiAPI.setPersonalGoal(newGoal);
      await loadAllData();
      setShowGoalDialog(false);
    } catch (error) {
      console.error('Failed to save goal:', error);
    } finally {
      setSavingGoal(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-6 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!kpiData) return null;

  const { performance, target, intake: currentIntake, personal_target, has_personal_goal } = kpiData;
  const isOnTrack = performance.status === 'on_track';
  const meetsPersonalGoal = performance.personal_status === 'on_track';

  // Get achievement icons
  const getAchievementIcon = (iconName) => {
    switch(iconName) {
      case 'trophy': return '🏆';
      case 'target': return '🎯';
      case 'trending-up': return '📈';
      case 'award': return '🏅';
      default: return '⭐';
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl rounded-xl border border-teal-300 overflow-hidden">
      {/* Header with Tabs */}
      <div className="p-4 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
              <Target className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">Your KPI Performance</h3>
              <p className="text-sm text-gray-500">{currentIntake} Intake</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {leaderboard?.your_rank && (
              <Badge className="bg-purple-100 text-purple-600 border-purple-500/30">
                Rank #{leaderboard.your_rank}
              </Badge>
            )}
            <Badge className={getStatusColor(performance.status)}>
              {isOnTrack ? 'On Track' : 'Below Target'}
            </Badge>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('performance')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'performance' 
                ? 'bg-teal-500 text-black' 
                : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
            }`}
          >
            Performance
          </button>
          <button
            onClick={() => setActiveTab('leaderboard')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'leaderboard' 
                ? 'bg-teal-500 text-black' 
                : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
            }`}
          >
            Leaderboard
          </button>
          <button
            onClick={() => setActiveTab('goals')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'goals' 
                ? 'bg-teal-500 text-black' 
                : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
            }`}
          >
            My Goal
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Performance Tab */}
        {activeTab === 'performance' && (
          <div className="space-y-4">
            {/* Main KPI */}
            <div className="text-center p-6 bg-[#e9ecef]/30 rounded-xl">
              <p className="text-sm text-gray-500 mb-2">Application to Enrollment Conversion</p>
              <p className={`text-5xl font-semibold ${isOnTrack ? 'text-green-600' : 'text-red-600'}`}>
                {performance.conversion_rate}%
              </p>
              
              {/* Dual target display */}
              <div className="flex justify-center gap-4 mt-2">
                <span className="text-sm text-gray-500">
                  Company: <span className={isOnTrack ? 'text-green-600' : 'text-red-600'}>{target}%</span>
                </span>
                {has_personal_goal && (
                  <span className="text-sm text-gray-500">
                    Personal: <span className={meetsPersonalGoal ? 'text-green-600' : 'text-teal-600'}>{personal_target}%</span>
                  </span>
                )}
              </div>
              
              <div className="mt-4 space-y-2">
                <Progress 
                  value={Math.min((performance.conversion_rate / target) * 100, 100)} 
                  className={`h-2 ${isOnTrack ? '[&>div]:bg-green-500' : '[&>div]:bg-red-500'}`}
                />
                {has_personal_goal && personal_target > target && (
                  <Progress 
                    value={Math.min((performance.conversion_rate / personal_target) * 100, 100)} 
                    className={`h-2 ${meetsPersonalGoal ? '[&>div]:bg-purple-500' : '[&>div]:bg-teal-500'}`}
                  />
                )}
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-[#e9ecef]/30 rounded-lg p-3 text-center">
                <FileText className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                <p className="text-xl font-semibold text-gray-800">{performance.total_applications}</p>
                <p className="text-xs text-gray-500">Applications</p>
              </div>
              <div className="bg-[#e9ecef]/30 rounded-lg p-3 text-center">
                <GraduationCap className="w-5 h-5 text-green-600 mx-auto mb-1" />
                <p className="text-xl font-semibold text-gray-800">{performance.enrolled}</p>
                <p className="text-xs text-gray-500">Enrolled</p>
              </div>
              <div className="bg-[#e9ecef]/30 rounded-lg p-3 text-center">
                <Target className="w-5 h-5 text-teal-600 mx-auto mb-1" />
                <p className="text-xl font-semibold text-gray-800">{performance.target_enrollments}</p>
                <p className="text-xs text-gray-500">Target</p>
              </div>
            </div>

            {/* Message */}
            <div className={`p-4 rounded-lg ${isOnTrack ? 'bg-green-500/10 border border-green-500/30' : 'bg-teal-500/10 border border-yellow-500/30'}`}>
              {isOnTrack ? (
                <p className="text-sm text-green-600 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Great job! You&apos;re meeting your KPI target.
                </p>
              ) : (
                <p className="text-sm text-teal-600 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  You need <strong className="text-gray-800">{performance.needed_to_hit_target}</strong> more enrollment{performance.needed_to_hit_target !== 1 ? 's' : ''} to hit your target.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && leaderboard && (
          <div className="space-y-4">
            {/* Your Position */}
            {leaderboard.your_data && (
              <div className="p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center text-2xl font-semibold text-purple-600 dark:text-purple-400">
                      #{leaderboard.your_rank}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 dark:text-white">Your Position</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {leaderboard.your_data.points} points • {leaderboard.your_data.conversion}% conversion
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {leaderboard.your_data.achievements?.map((ach, i) => (
                      <span key={i} className="text-xl" title={ach.name}>
                        {getAchievementIcon(ach.icon)}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Top 10 - GDPR Compliant */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Top Performers</h4>
              {leaderboard.leaderboard?.slice(0, 10).map((entry, idx) => (
                <div 
                  key={idx} 
                  className={`p-3 rounded-lg flex items-center justify-between ${
                    entry.is_you 
                      ? 'bg-teal-500/10 border border-yellow-500/30' 
                      : 'bg-[#e9ecef]/30 dark:bg-slate-700/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                      entry.rank === 1 ? 'bg-teal-100 dark:bg-teal-900/50 text-teal-600 dark:text-teal-400' :
                      entry.rank === 2 ? 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300' :
                      entry.rank === 3 ? 'bg-orange-100 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400' :
                      'bg-gray-200/50 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400'
                    }`}>
                      {entry.rank}
                    </div>
                    <div>
                      <p className={`font-medium ${entry.is_you ? 'text-teal-600 dark:text-teal-400' : 'text-gray-800 dark:text-white'}`}>
                        {entry.agent_name} {entry.is_you && '(You)'}
                      </p>
                      {/* GDPR: Only show conversion for self, hide for others */}
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        {entry.is_anonymized ? 'Performance hidden for privacy' : `${entry.conversion}% conversion`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!entry.is_anonymized && (
                      <>
                        <div className="flex gap-0.5">
                          {entry.achievements?.slice(0, 3).map((ach, i) => (
                            <span key={i} className="text-sm" title={ach.name}>
                              {getAchievementIcon(ach.icon)}
                            </span>
                          ))}
                        </div>
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{entry.points} pts</span>
                      </>
                    )}
                    {entry.is_anonymized && (
                      <span className="text-xs text-gray-400 dark:text-gray-500">{entry.achievement_count || 0} badges</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <p className="text-xs text-center text-gray-400 dark:text-gray-500">
              {leaderboard.total_participants} agents competing this intake
            </p>
          </div>
        )}

        {/* Goals Tab */}
        {activeTab === 'goals' && (
          <div className="space-y-4">
            {/* Current Goal Display */}
            <div className="p-6 bg-[#e9ecef]/30 rounded-xl text-center">
              <Award className="w-12 h-12 text-teal-600 mx-auto mb-3" />
              <h4 className="text-lg font-semibold text-gray-800 mb-2">
                {has_personal_goal ? 'Your Personal Goal' : 'Set Your Goal'}
              </h4>
              
              {has_personal_goal ? (
                <>
                  <p className="text-4xl font-semibold text-teal-600 mb-2">{personal_target}%</p>
                  <p className="text-sm text-gray-500">
                    Conversion target (Company minimum: {target}%)
                  </p>
                  <div className="mt-4">
                    <Progress 
                      value={Math.min((performance.conversion_rate / personal_target) * 100, 100)} 
                      className={`h-3 ${meetsPersonalGoal ? '[&>div]:bg-green-500' : '[&>div]:bg-teal-500'}`}
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      {performance.conversion_rate}% of {personal_target}% target
                    </p>
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-500">
                  Challenge yourself by setting a personal goal above the {target}% company minimum!
                </p>
              )}
            </div>

            {/* Goal Setting Form */}
            <div className="p-4 bg-white rounded-lg border border-white/5">
              <h5 className="text-sm font-medium text-gray-800 mb-3">
                {has_personal_goal ? 'Update Your Goal' : 'Set a Personal Goal'}
              </h5>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-xs text-gray-500 block mb-1">Target Conversion Rate</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min="8"
                      max="50"
                      value={newGoal}
                      onChange={(e) => setNewGoal(Number(e.target.value))}
                      className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <span className="text-xl font-semibold text-teal-600 w-16 text-center">
                      {newGoal}%
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>8% (min)</span>
                    <span>50%</span>
                  </div>
                </div>
              </div>
              
              <Button
                onClick={handleSaveGoal}
                disabled={savingGoal || (has_personal_goal && newGoal === personal_target)}
                className="w-full mt-4 bg-teal-500 hover:bg-yellow-600 text-black"
              >
                {savingGoal ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</>
                ) : has_personal_goal ? (
                  'Update Goal'
                ) : (
                  'Set My Goal'
                )}
              </Button>
            </div>

            {/* Motivational Message */}
            <div className="p-3 bg-gradient-to-r from-yellow-500/5 to-orange-500/5 rounded-lg border border-yellow-500/20">
              <p className="text-xs text-gray-500">
                💡 <strong className="text-gray-600">Pro Tip:</strong> Setting a personal goal above the company minimum earns you bonus points on the leaderboard!
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Employee KPI Panel (for Admin/Employee view)
export const EmployeeKPIPanel = ({ employeeId, showDetails = true }) => {
  const [kpiData, setKpiData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadKPI();
  }, [employeeId]);

  const loadKPI = async () => {
    setLoading(true);
    try {
      const res = await kpiAPI.getEmployeeKPI(employeeId);
      setKpiData(res.data);
    } catch (error) {
      console.error('Failed to load KPI:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!kpiData) return null;

  return (
    <div className="space-y-4">
      {/* B2B Performance */}
      <KPICard
        title="B2B (Agent-Led) Conversion"
        value={kpiData.b2b_performance.conversion_rate}
        target={kpiData.targets.b2b_app_to_enrollment}
        icon={Briefcase}
        status={kpiData.b2b_performance.status}
        gap={kpiData.b2b_performance.gap}
        subtitle={`${kpiData.b2b_performance.enrolled}/${kpiData.b2b_performance.total_applications} enrolled • ${kpiData.b2b_performance.agents_managed} agents managed`}
      />

      {/* B2C App to Enrollment */}
      <KPICard
        title="B2C App to Enrollment"
        value={kpiData.b2c_performance.app_to_enrollment.conversion_rate}
        target={kpiData.targets.b2c_app_to_enrollment}
        icon={Users}
        status={kpiData.b2c_performance.app_to_enrollment.status}
        gap={kpiData.b2c_performance.app_to_enrollment.gap}
        subtitle={`${kpiData.b2c_performance.enrolled}/${kpiData.b2c_performance.total_applications} enrolled`}
      />

      {/* B2C Inquiry to Lead */}
      <KPICard
        title="B2C Inquiry to Lead Validation"
        value={kpiData.b2c_performance.inquiry_to_lead.conversion_rate}
        target={kpiData.targets.b2c_inquiry_to_lead}
        icon={TrendingUp}
        status={kpiData.b2c_performance.inquiry_to_lead.status}
        gap={kpiData.b2c_performance.inquiry_to_lead.gap}
        subtitle={`${kpiData.b2c_performance.inquiry_to_lead.leads_validated} leads validated`}
      />
    </div>
  );
};

// Admin KPI Overview Panel
export const AdminKPIOverview = () => {
  const [kpiData, setKpiData] = useState(null);
  const [employeesKPI, setEmployeesKPI] = useState(null);
  const [agentsKPI, setAgentsKPI] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadAllKPI();
  }, []);

  const loadAllKPI = async () => {
    setLoading(true);
    try {
      const [overviewRes, employeesRes, agentsRes] = await Promise.all([
        kpiAPI.getOverview(),
        kpiAPI.getAllEmployeesKPI(),
        kpiAPI.getAllAgentsKPI()
      ]);
      setKpiData(overviewRes.data);
      setEmployeesKPI(employeesRes.data);
      setAgentsKPI(agentsRes.data);
    } catch (error) {
      console.error('Failed to load KPI:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-8 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!kpiData) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center">
            <BarChart2 className="w-6 h-6 text-teal-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-800">KPI Performance Dashboard</h2>
            <p className="text-gray-500">{kpiData.intake} Intake</p>
          </div>
        </div>
        <Button variant="outline" onClick={loadAllKPI} className="border-gray-300 text-gray-600">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {['overview', 'employees', 'agents'].map((tab) => (
          <Button
            key={tab}
            variant={activeTab === tab ? 'default' : 'outline'}
            onClick={() => setActiveTab(tab)}
            className={activeTab === tab ? 'bg-teal-500 text-gray-900' : 'border-gray-300 text-gray-600'}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </Button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-2 gap-4">
          {/* B2B Performance */}
          <KPICard
            title="B2B (Agent-Led) Conversion"
            value={kpiData.b2b_performance.conversion_rate}
            target={kpiData.targets.b2b_app_to_enrollment}
            icon={Briefcase}
            status={kpiData.b2b_performance.status}
            gap={kpiData.b2b_performance.gap}
            subtitle={`${kpiData.b2b_performance.enrolled}/${kpiData.b2b_performance.total_applications} applications enrolled`}
          />

          {/* B2C App to Enrollment */}
          <KPICard
            title="B2C App to Enrollment"
            value={kpiData.b2c_performance.app_to_enrollment.conversion_rate}
            target={kpiData.targets.b2c_app_to_enrollment}
            icon={Users}
            status={kpiData.b2c_performance.app_to_enrollment.status}
            gap={kpiData.b2c_performance.app_to_enrollment.gap}
            subtitle={`${kpiData.b2c_performance.enrolled}/${kpiData.b2c_performance.total_applications} applications enrolled`}
          />

          {/* B2C Inquiry to Lead */}
          <KPICard
            title="B2C Inquiry to Lead"
            value={kpiData.b2c_performance.inquiry_to_lead.conversion_rate}
            target={kpiData.targets.b2c_inquiry_to_lead}
            icon={TrendingUp}
            status={kpiData.b2c_performance.inquiry_to_lead.status}
            gap={kpiData.b2c_performance.inquiry_to_lead.gap}
            subtitle={`${kpiData.b2c_performance.inquiry_to_lead.leads_validated}/${kpiData.b2c_performance.inquiry_to_lead.total_inquiries} inquiries validated`}
          />

          {/* Overall Summary */}
          <div className="rounded-xl p-5 border bg-[#e9ecef]/30 border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <Award className="w-5 h-5 text-teal-600" />
              <p className="font-semibold text-gray-800">Overall Performance</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <p className="text-3xl font-semibold text-gray-800">{kpiData.overall_summary.total_applications}</p>
                <p className="text-sm text-gray-500">Total Apps</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-semibold text-green-600">{kpiData.overall_summary.total_enrolled}</p>
                <p className="text-sm text-gray-500">Enrolled</p>
              </div>
            </div>
            <div className="mt-3 text-center">
              <p className="text-2xl font-semibold text-teal-600">{kpiData.overall_summary.overall_conversion}%</p>
              <p className="text-sm text-gray-500">Overall Conversion</p>
            </div>
          </div>
        </div>
      )}

      {/* Employees Tab */}
      {activeTab === 'employees' && employeesKPI && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#e9ecef]/50">
                <tr>
                  <th className="text-left p-4 text-gray-600 font-medium">Employee</th>
                  <th className="text-center p-4 text-gray-600 font-medium">B2B Conv.</th>
                  <th className="text-center p-4 text-gray-600 font-medium">B2C Conv.</th>
                  <th className="text-center p-4 text-gray-600 font-medium">Inquiry→Lead</th>
                  <th className="text-center p-4 text-gray-600 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {employeesKPI.employees.map((emp) => (
                  <tr key={emp.employee_id} className="border-t border-white/5 hover:bg-gray-50">
                    <td className="p-4">
                      <p className="font-medium text-gray-800">{emp.employee_name}</p>
                      <p className="text-sm text-gray-500">{emp.role} • {emp.agents_managed} agents</p>
                    </td>
                    <td className="p-4 text-center">
                      <span className={emp.b2b.status === 'on_track' ? 'text-green-600' : 'text-red-600'}>
                        {emp.b2b.conversion}%
                      </span>
                      <span className="text-gray-400 text-sm"> / {emp.b2b.target}%</span>
                    </td>
                    <td className="p-4 text-center">
                      <span className={emp.b2c.status === 'on_track' ? 'text-green-600' : 'text-red-600'}>
                        {emp.b2c.conversion}%
                      </span>
                      <span className="text-gray-400 text-sm"> / {emp.b2c.target}%</span>
                    </td>
                    <td className="p-4 text-center">
                      <span className={emp.b2c.inquiry_to_lead >= emp.b2c.inquiry_target ? 'text-green-600' : 'text-red-600'}>
                        {emp.b2c.inquiry_to_lead}%
                      </span>
                      <span className="text-gray-400 text-sm"> / {emp.b2c.inquiry_target}%</span>
                    </td>
                    <td className="p-4 text-center">
                      <Badge className={getStatusColor(emp.overall_status)}>
                        {emp.overall_status === 'on_track' ? 'On Track' : 'Needs Attention'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Agents Tab */}
      {activeTab === 'agents' && agentsKPI && (
        <div className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-4 text-center">
              <p className="text-2xl font-semibold text-gray-800">{agentsKPI.summary.total_agents}</p>
              <p className="text-sm text-gray-500">Total Agents</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-4 text-center border border-green-500/30">
              <p className="text-2xl font-semibold text-green-600">{agentsKPI.summary.on_track}</p>
              <p className="text-sm text-gray-500">On Track</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-4 text-center border border-red-500/30">
              <p className="text-2xl font-semibold text-red-600">{agentsKPI.summary.below_target}</p>
              <p className="text-sm text-gray-500">Below Target</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-4 text-center">
              <p className="text-2xl font-semibold text-gray-500">{agentsKPI.summary.no_applications}</p>
              <p className="text-sm text-gray-500">No Applications</p>
            </div>
          </div>

          {/* Agents Table */}
          <div className="bg-white border border-gray-200 rounded-xl rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#e9ecef]/50">
                <tr>
                  <th className="text-left p-4 text-gray-600 font-medium">Agent</th>
                  <th className="text-center p-4 text-gray-600 font-medium">Applications</th>
                  <th className="text-center p-4 text-gray-600 font-medium">Enrolled</th>
                  <th className="text-center p-4 text-gray-600 font-medium">Conversion</th>
                  <th className="text-center p-4 text-gray-600 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {agentsKPI.agents.slice(0, 15).map((agent) => (
                  <tr key={agent.agent_id} className="border-t border-white/5 hover:bg-gray-50">
                    <td className="p-4">
                      <p className="font-medium text-gray-800">{agent.agent_name}</p>
                      <p className="text-sm text-gray-500">{agent.company}</p>
                    </td>
                    <td className="p-4 text-center text-gray-800">{agent.applications}</td>
                    <td className="p-4 text-center text-green-600">{agent.enrolled}</td>
                    <td className="p-4 text-center">
                      <span className={agent.status === 'on_track' ? 'text-green-600' : 'text-red-600'}>
                        {agent.conversion}%
                      </span>
                      <span className="text-gray-400 text-sm"> / {agent.target}%</span>
                    </td>
                    <td className="p-4 text-center">
                      <Badge className={getStatusColor(agent.status)}>
                        {agent.status === 'on_track' ? 'On Track' : agent.applications === 0 ? 'No Apps' : 'Below Target'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* KPI Targets Reference */}
      <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-4">
        <h3 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
          <Target className="w-4 h-4 text-teal-600" /> KPI Targets (Per Intake)
        </h3>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div className="bg-[#e9ecef]/50 rounded-lg p-3">
            <p className="text-teal-600 font-semibold">8%</p>
            <p className="text-gray-500">B2B Agent-Led Conversion</p>
          </div>
          <div className="bg-[#e9ecef]/50 rounded-lg p-3">
            <p className="text-teal-600 font-semibold">12%</p>
            <p className="text-gray-500">B2C App to Enrollment</p>
          </div>
          <div className="bg-[#e9ecef]/50 rounded-lg p-3">
            <p className="text-teal-600 font-semibold">25%</p>
            <p className="text-gray-500">B2C Inquiry to Lead</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default { AgentKPIPanel, EmployeeKPIPanel, AdminKPIOverview };
